import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { StripeService, UserSubscription } from '../services/stripeService';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

export const SubscriptionManagement: React.FC = () => {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stripeServiceRef = React.useRef(new StripeService());
  const stripeService = stripeServiceRef.current;

  const loadSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const sub = await stripeService.getUserSubscription();
      setSubscription(sub);
    } catch (err: any) {
      console.error('Error loading subscription:', err);
      setError(err.message || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [stripeService]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      const url = await stripeService.createPortalSession();
      window.location.href = url;
    } catch (err: any) {
      console.error('Error opening portal:', err);
      setError(err.message || 'Failed to open subscription management');
      setActionLoading(false);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Active' },
      trialing: { icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Trialing' },
      past_due: { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Past Due' },
      canceled: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-50', label: 'Canceled' },
      unpaid: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Unpaid' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.canceled;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Active Subscription</CardTitle>
          <CardDescription>Subscribe to get full access to all features</CardDescription>
        </CardHeader>
        <CardFooter>
          <Button onClick={() => window.location.href = '/subscription'}>
            View Plans
          </Button>
        </CardFooter>
      </Card>
    );
  }

  const plan = stripeService.getPlanById(subscription.plan_id);

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>
                {plan ? `${plan.name} Plan` : subscription.plan_id}
              </CardDescription>
            </div>
            {getStatusBadge(subscription.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Current Period</p>
            <p className="text-lg">
              {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
            </p>
          </div>

          {plan && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Billing Amount</p>
              <p className="text-lg">
                {stripeService.formatPrice(plan.price)} / {plan.interval === 'month' ? 'month' : 'year'}
              </p>
            </div>
          )}

          {subscription.cancel_at_period_end && (
            <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <p className="text-sm text-yellow-800">
                Your subscription will cancel on {formatDate(subscription.current_period_end)}.
                You will continue to have access until then.
              </p>
            </div>
          )}

          {subscription.canceled_at && !subscription.cancel_at_period_end && (
            <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-sm text-gray-800">
                Your subscription was canceled on {formatDate(subscription.canceled_at)}.
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleManageSubscription}
            disabled={actionLoading}
            variant="default"
            className="w-full"
          >
            {actionLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Manage Subscription'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

